/**
 * Teslyn Cloud's hazard pack calls (Diurnal parity 06-034; docs/specs/cloud/hazard-packs.md;
 * 20261027010000_hazard_packs.sql, 20261105100000_hazard_maps_sync.sql): the organisation's packs with their projects,
 * what went out and their links (`my_hazard_packs`, read through the staff policies), "Publish for Oz Runways",
 * withdrawing a link and making a new one; and the public landing page's details from the `hazard-pack` function.
 * What a person may do is decided by the database.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  hazardPackUrls,
  type HazardPackLinkState,
  type HazardPackMeta,
  type HazardPackStats,
} from '@teslyn/domain';

export interface PackLinkRow {
  readonly id: string;
  readonly token: string;
  readonly created_at: string;
  readonly created_by_name: string;
  readonly expires_at: string | null;
  readonly revoked_at: string | null;
  readonly revoked_by_name: string;
  readonly last_fetched_at: string | null;
  readonly fetch_count: number;
  readonly state: HazardPackLinkState;
}

/** What went out to an audience: which version, when, by whom. */
export interface PackRelease {
  readonly version: number;
  readonly published_at: string;
  readonly published_by_name: string;
}

export interface PackRow {
  readonly id: string;
  readonly project_id: string;
  readonly project_number: string;
  readonly project_title: string;
  readonly project_status: string;
  readonly name: string;
  readonly version: number;
  readonly size_bytes: number;
  readonly stats: HazardPackStats;
  /** When this version reached Teslyn Cloud from Teslyn Field, and from whom. */
  readonly published_at: string;
  readonly published_by_name: string;
  /** What the Oz Runways link serves; null when nothing was published. */
  readonly oz?: PackRelease | null;
  /** What the client sees; null when nothing was shown. */
  readonly client?: PackRelease | null;
  /** Newest first. */
  readonly links: readonly PackLinkRow[];
}

const fail = (e: { message: string } | null): void => {
  if (e) throw new Error(e.message);
};

export async function loadHazardPacks(sb: SupabaseClient, tenantId: string): Promise<PackRow[]> {
  const { data, error } = await sb.rpc('my_hazard_packs', { p_tenant: tenantId });
  fail(error);
  return (Array.isArray(data) ? data : []) as PackRow[];
}

/** "Publish for Oz Runways": the current synced pack goes out behind the link, made when none works (decision 1042). */
export async function publishPackForOzRunways(sb: SupabaseClient, projectId: string): Promise<void> {
  const { error } = await sb.rpc('hazard_pack_oz_publish', { p_project: projectId });
  fail(error);
}

/** Withdraw a link: it never works again (decision 88). */
export async function revokePackLink(sb: SupabaseClient, linkId: string): Promise<PackLinkRow> {
  const { data, error } = await sb.rpc('hazard_pack_link_revoke', { p_link: linkId });
  fail(error);
  return data as PackLinkRow;
}

/** A new link for a pack whose link was withdrawn or expired (the working one when there is one). */
export async function newPackLink(sb: SupabaseClient, packId: string): Promise<PackLinkRow> {
  const { data, error } = await sb.rpc('hazard_pack_link_create', { p_pack: packId });
  fail(error);
  return data as PackLinkRow;
}

/** What the landing page shows: the details, or why the link no longer works. */
export type LandingAnswer =
  | { readonly kind: 'pack'; readonly meta: HazardPackMeta }
  | { readonly kind: 'unknown'; readonly message: string };

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/** The landing page's details from the `hazard-pack` function (no sign-in). */
export async function loadLanding(
  supabaseUrl: string,
  token: string,
  fetchImpl: Fetch = (u, i) => fetch(u, i),
): Promise<LandingAnswer> {
  const res = await fetchImpl(hazardPackUrls(supabaseUrl, token).meta, {
    headers: { Accept: 'application/json' },
  });
  const body = (await res.json().catch(() => null)) as (HazardPackMeta & { error?: string }) | null;
  if (res.status === 404 || !body)
    return {
      kind: 'unknown',
      message: body?.error ?? 'This hazard pack link is not known. Ask the office for the link.',
    };
  if (!res.ok && res.status !== 410)
    throw new Error(`Teslyn Cloud answered ${res.status}. Try again shortly.`);
  return { kind: 'pack', meta: body };
}
